import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Briefcase, Save, AlertCircle } from 'lucide-react';
import { jobAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

interface JobFormData {
  title: string;
  companyName: string;
  location: string;
  employmentType: string;
  experienceLevel: string;
  salaryRange: {
    min: number;
    max: number;
    currency: string;
    period: string;
  };
  description: string;
  requiredSkills: string;
  qualifications: string;
  jobCategory: string;
  numberOfOpenings: number;
  lastDateToApply: string;
  contactEmail: string;
  contactPhone: string;
}

interface FormErrors {
  [key: string]: string;
}

export default function PostJob() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState<JobFormData>({
    title: '',
    companyName: '',
    location: '',
    employmentType: '',
    experienceLevel: '',
    salaryRange: {
      min: 0,
      max: 0,
      currency: 'INR',
      period: 'per month'
    },
    description: '',
    requiredSkills: '',
    qualifications: '',
    jobCategory: '',
    numberOfOpenings: 1,
    lastDateToApply: '',
    contactEmail: user?.email || '',
    contactPhone: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const jobCategories = [
    'IT/Software',
    'Engineering',
    'Banking/Finance',
    'Marketing/Sales',
    'Healthcare',
    'Education',
    'Manufacturing',
    'Retail',
    'Government',
    'Non-profit',
    'Other'
  ];

  const employmentTypes = [
    'Full-time',
    'Part-time',
    'Contract',
    'Internship',
    'Freelance'
  ];

  const experienceLevels = [
    'Fresher',
    '1-2 years',
    '3-5 years',
    '6-10 years',
    '10+ years'
  ];

  const salaryPeriods = [
    'per month',
    'per year',
    'per hour'
  ];

  const validateField = (name: string, value: any) => {
    let error = '';

    switch (name) {
      case 'title':
        if (!value || String(value).trim().length === 0) {
          error = 'Job title is required';
        } else if (String(value).length > 200) {
          error = 'Job title cannot exceed 200 characters';
        }
        break;

      case 'companyName':
        if (!value || String(value).trim().length === 0) {
          error = 'Company name is required';
        } else if (String(value).length > 100) {
          error = 'Company name cannot exceed 100 characters';
        }
        break;

      case 'location':
        if (!value || String(value).trim().length === 0) {
          error = 'Location is required';
        } else if (String(value).length > 100) {
          error = 'Location cannot exceed 100 characters';
        }
        break;

      case 'jobCategory':
        if (!value) {
          error = 'Job category is required';
        }
        break;

      case 'salaryMin':
        if (formData.salaryRange.max > 0 && Number(value) > formData.salaryRange.max) {
          error = 'Minimum salary cannot be greater than maximum salary';
        }
        break;

      case 'salaryMax':
        if (formData.salaryRange.min > 0 && Number(value) < formData.salaryRange.min) {
          error = 'Maximum salary cannot be less than minimum salary';
        }
        break;

      case 'description':
        if (value && String(value).length > 5000) {
          error = 'Job description cannot exceed 5000 characters';
        }
        break;

      case 'qualifications':
        if (value && String(value).length > 2000) {
          error = 'Qualifications cannot exceed 2000 characters';
        }
        break;

      case 'numberOfOpenings':
        if (value && Number(value) < 1) {
          error = 'Number of openings must be at least 1';
        }
        break;

      case 'lastDateToApply':
        if (value && new Date(String(value)) <= new Date()) {
          error = 'Last date to apply must be in the future';
        }
        break;

      case 'contactEmail':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value && !emailRegex.test(String(value))) {
          error = 'Please enter a valid email address';
        }
        break;

      case 'contactPhone':
        const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
        if (value && !phoneRegex.test(String(value))) {
          error = 'Please enter a valid phone number';
        }
        break;
    }

    setErrors(prev => ({ ...prev, [name]: error }));
    return error === '';
  };

  const handleInputChange = (name: string, value: any) => {
    if (name.startsWith('salary')) {
      const field = name.replace('salary', '').toLowerCase();
      setFormData(prev => ({
        ...prev,
        salaryRange: {
          ...prev.salaryRange,
          [field]: field === 'min' || field === 'max' ? Number(value) : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'numberOfOpenings' ? Number(value) : value
      }));
    }
  };

  const handleFieldBlur = (name: string) => {
    let value: any;
    
    if (name.startsWith('salary')) {
      value = formData.salaryRange[name.replace('salary', '').toLowerCase() as keyof typeof formData.salaryRange];
    } else {
      value = formData[name as keyof typeof formData];
    }
    
    validateField(name, String(value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate mandatory and filled fields
    const fieldValidations = [
      ['title', formData.title],
      ['companyName', formData.companyName],
      ['location', formData.location],
      ['jobCategory', formData.jobCategory],
      ['salaryMin', String(formData.salaryRange.min)],
      ['salaryMax', String(formData.salaryRange.max)],
      ['description', formData.description],
      ['qualifications', formData.qualifications],
      ['numberOfOpenings', String(formData.numberOfOpenings)],
      ['lastDateToApply', formData.lastDateToApply],
      ['contactEmail', formData.contactEmail],
      ['contactPhone', formData.contactPhone]
    ];

    let hasErrors = false;
    fieldValidations.forEach(([name, value]) => {
      // Always validate mandatory fields (title, companyName, location, jobCategory)
      if (['title', 'companyName', 'location', 'jobCategory'].includes(name)) {
        if (!validateField(name, value)) {
          hasErrors = true;
        }
      } else if (value && !validateField(name, value)) {
        // Only validate other fields if they have values
        hasErrors = true;
      }
    });

    // Check that at least one contact method is provided
    if (!formData.contactEmail && !formData.contactPhone) {
      setError('Please provide either contact email or phone number');
      hasErrors = true;
    }

    if (hasErrors) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    try {
      setLoading(true);

      // Clean and prepare job data
      const jobData = {
        ...formData,
        requiredSkills: formData.requiredSkills 
          ? formData.requiredSkills.split(',').map(skill => skill.trim()).filter(skill => skill)
          : [],
        // Clean salary range - only include if values are provided
        salaryRange: {
          ...formData.salaryRange,
          min: formData.salaryRange.min || undefined,
          max: formData.salaryRange.max || undefined
        }
      };

      // Remove empty string fields to avoid validation issues
      Object.keys(jobData).forEach(key => {
        if (jobData[key as keyof typeof jobData] === '') {
          delete jobData[key as keyof typeof jobData];
        }
      });

      console.log('Sending job data:', jobData);
      const response = await jobAPI.createJob(jobData);
      
      setSuccess('Job posted successfully! It will be reviewed by admin before going live.');
      
      // Redirect to jobs page after 2 seconds
      setTimeout(() => {
        navigate('/jobs');
      }, 2000);

    } catch (err: any) {
      console.error('Job posting error:', err);
      console.error('Error response:', err.response?.data);
      
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        // Show validation errors from backend
        const errorMessages = err.response.data.errors.map((error: any) => error.msg || error.message).join(', ');
        setError(`Validation failed: ${errorMessages}`);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to post job. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/jobs')}
            className="mb-4 text-gray-600 hover:text-secondary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Jobs
          </Button>
          
          <div className="flex items-center space-x-3">
            <Briefcase className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold text-secondary">Post a New Job</h1>
              <p className="text-gray-600 mt-1">Fill in the details to post your job opportunity</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl font-semibold">Job Details</CardTitle>
            <CardDescription>
              Provide comprehensive information about the job position
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="title">Job Title *</Label>
                  <Input
                    id="title"
                    placeholder="e.g., Senior Software Engineer"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    onBlur={() => handleFieldBlur('title')}
                    className="border-gray-300 focus:border-green-500"
                  />
                  {errors.title && <p className="text-red-500 text-sm">{errors.title}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name *</Label>
                  <Input
                    id="companyName"
                    placeholder="e.g., Tech Solutions Pvt Ltd"
                    value={formData.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    onBlur={() => handleFieldBlur('companyName')}
                    className="border-gray-300 focus:border-green-500"
                  />
                  {errors.companyName && <p className="text-red-500 text-sm">{errors.companyName}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    id="location"
                    placeholder="e.g., Hyderabad, Telangana"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    onBlur={() => handleFieldBlur('location')}
                    className="border-gray-300 focus:border-green-500"
                  />
                  {errors.location && <p className="text-red-500 text-sm">{errors.location}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="jobCategory">Job Category *</Label>
                  <Select value={formData.jobCategory} onValueChange={(value) => handleInputChange('jobCategory', value)}>
                    <SelectTrigger className="border-gray-300 focus:border-green-500">
                      <SelectValue placeholder="Select job category" />
                    </SelectTrigger>
                    <SelectContent className="z-[99999] bg-surface border shadow-lg">
                      {jobCategories.map(category => (
                        <SelectItem key={category} value={category}>{category}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.jobCategory && <p className="text-red-500 text-sm">{errors.jobCategory}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="employmentType">Employment Type</Label>
                  <Select value={formData.employmentType} onValueChange={(value) => handleInputChange('employmentType', value)}>
                    <SelectTrigger className="border-gray-300 focus:border-green-500">
                      <SelectValue placeholder="Select employment type" />
                    </SelectTrigger>
                    <SelectContent className="z-[99999] bg-surface border shadow-lg">
                      {employmentTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.employmentType && <p className="text-red-500 text-sm">{errors.employmentType}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="experienceLevel">Experience Level</Label>
                  <Select value={formData.experienceLevel} onValueChange={(value) => handleInputChange('experienceLevel', value)}>
                    <SelectTrigger className="border-gray-300 focus:border-green-500">
                      <SelectValue placeholder="Select experience level" />
                    </SelectTrigger>
                    <SelectContent className="z-[99999] bg-surface border shadow-lg">
                      {experienceLevels.map(level => (
                        <SelectItem key={level} value={level}>{level}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.experienceLevel && <p className="text-red-500 text-sm">{errors.experienceLevel}</p>}
                </div>
              </div>

              {/* Salary Information */}
              <div className="space-y-4">
                <Label className="text-base font-medium">Salary Range</Label>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="salaryMin">Minimum</Label>
                    <Input
                      id="salaryMin"
                      type="number"
                      placeholder="25000"
                      value={formData.salaryRange.min || ''}
                      onChange={(e) => handleInputChange('salaryMin', e.target.value)}
                      onBlur={() => handleFieldBlur('salaryMin')}
                      className="border-gray-300 focus:border-green-500"
                    />
                    {errors.salaryMin && <p className="text-red-500 text-sm">{errors.salaryMin}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="salaryMax">Maximum</Label>
                    <Input
                      id="salaryMax"
                      type="number"
                      placeholder="45000"
                      value={formData.salaryRange.max || ''}
                      onChange={(e) => handleInputChange('salaryMax', e.target.value)}
                      onBlur={() => handleFieldBlur('salaryMax')}
                      className="border-gray-300 focus:border-green-500"
                    />
                    {errors.salaryMax && <p className="text-red-500 text-sm">{errors.salaryMax}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="salaryCurrency">Currency</Label>
                    <Input
                      id="salaryCurrency"
                      value={formData.salaryRange.currency}
                      onChange={(e) => handleInputChange('salaryCurrency', e.target.value)}
                      className="border-gray-300 focus:border-green-500"
                      disabled
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="salaryPeriod">Period</Label>
                    <Select value={formData.salaryRange.period} onValueChange={(value) => handleInputChange('salaryPeriod', value)}>
                      <SelectTrigger className="border-gray-300 focus:border-green-500">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[99999] bg-surface border shadow-lg">
                        {salaryPeriods.map(period => (
                          <SelectItem key={period} value={period}>{period}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Job Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Job Description</Label>
                <Textarea
                  id="description"
                  placeholder="Provide detailed information about the job role, responsibilities, and requirements..."
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  onBlur={() => handleFieldBlur('description')}
                  className="border-gray-300 focus:border-green-500 min-h-32"
                  rows={6}
                />
                {errors.description && <p className="text-red-500 text-sm">{errors.description}</p>}
                <p className="text-sm text-gray-500">
                  {formData.description.length}/5000 characters
                </p>
              </div>

              {/* Required Skills */}
              <div className="space-y-2">
                <Label htmlFor="requiredSkills">Required Skills</Label>
                <Input
                  id="requiredSkills"
                  placeholder="e.g., React, Node.js, MongoDB, AWS"
                  value={formData.requiredSkills}
                  onChange={(e) => handleInputChange('requiredSkills', e.target.value)}
                  onBlur={() => handleFieldBlur('requiredSkills')}
                  className="border-gray-300 focus:border-green-500"
                />
                {errors.requiredSkills && <p className="text-red-500 text-sm">{errors.requiredSkills}</p>}
                <p className="text-sm text-gray-500">Separate multiple skills with commas</p>
              </div>

              {/* Qualifications */}
              <div className="space-y-2">
                <Label htmlFor="qualifications">Qualifications</Label>
                <Textarea
                  id="qualifications"
                  placeholder="e.g., Bachelor's degree in Computer Science, 3+ years of experience in web development..."
                  value={formData.qualifications}
                  onChange={(e) => handleInputChange('qualifications', e.target.value)}
                  onBlur={() => handleFieldBlur('qualifications')}
                  className="border-gray-300 focus:border-green-500 min-h-24"
                  rows={4}
                />
                {errors.qualifications && <p className="text-red-500 text-sm">{errors.qualifications}</p>}
                <p className="text-sm text-gray-500">
                  {formData.qualifications.length}/2000 characters (minimum 10 required)
                </p>
              </div>

              {/* Additional Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="numberOfOpenings">Number of Openings</Label>
                  <Input
                    id="numberOfOpenings"
                    type="number"
                    min="1"
                    placeholder="1"
                    value={formData.numberOfOpenings}
                    onChange={(e) => handleInputChange('numberOfOpenings', e.target.value)}
                    onBlur={() => handleFieldBlur('numberOfOpenings')}
                    className="border-gray-300 focus:border-green-500"
                  />
                  {errors.numberOfOpenings && <p className="text-red-500 text-sm">{errors.numberOfOpenings}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastDateToApply">Last Date to Apply</Label>
                  <Input
                    id="lastDateToApply"
                    type="date"
                    value={formData.lastDateToApply}
                    onChange={(e) => handleInputChange('lastDateToApply', e.target.value)}
                    onBlur={() => handleFieldBlur('lastDateToApply')}
                    className="border-gray-300 focus:border-green-500"
                  />
                  {errors.lastDateToApply && <p className="text-red-500 text-sm">{errors.lastDateToApply}</p>}
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-2">
                <Label className="text-base font-medium">Contact Information *</Label>
                <p className="text-sm text-gray-500">Please provide at least one contact method</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="contactEmail">Contact Email</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    placeholder="hr@company.com"
                    value={formData.contactEmail}
                    onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                    onBlur={() => handleFieldBlur('contactEmail')}
                    className="border-gray-300 focus:border-green-500"
                  />
                  {errors.contactEmail && <p className="text-red-500 text-sm">{errors.contactEmail}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactPhone">Contact Phone</Label>
                  <Input
                    id="contactPhone"
                    type="tel"
                    placeholder="+91 9876543210"
                    value={formData.contactPhone}
                    onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                    onBlur={() => handleFieldBlur('contactPhone')}
                    className="border-gray-300 focus:border-green-500"
                  />
                  {errors.contactPhone && <p className="text-red-500 text-sm">{errors.contactPhone}</p>}
                </div>
              </div>

              {/* Error/Success Messages */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  {error}
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md flex items-center">
                  <Save className="w-5 h-5 mr-2" />
                  {success}
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/jobs')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-primary hover:bg-primary-dark text-white"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Posting Job...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Save className="w-4 h-4 mr-2" />
                      Post Job
                    </div>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 